import { Component, OnInit } from '@angular/core';
import { DomSanitizer }      from '@angular/platform-browser';
import { NgbModal }          from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }  from '@ngx-translate/core';
import { UIRouter }          from '@uirouter/core';

import { ApiService }                  from '../api/api.service';
import { BusinessIntelligenceService } from '../business-intelligence/business-intelligence.service';
import { CompanyService }              from '../companies/company.service';
import { ConfirmService }              from '../messages/confirm/confirm.service';
import { MessagesService }             from '../messages/messages.service';
import { MyobService }                 from '../myob/myob.service';
import { PluralizePipe }               from '../shared/pipes/pluralize.pipe';
import { QuickbooksService }           from '../quickbooks/quickbooks.service';
import { SessionService }              from '../sessions/session.service';
import { SessionWarningModel }         from '../sessions/models/session.warning';
import { SidebarService }              from '../sidebar/sidebar.service';
import { XeroService }                 from '../xero/xero.service';

import userProfileIcon from 'icons/user-profile.svg';

@Component({
  selector   : 'wor-header',
  template: require('./header.component.html')
})
export class HeaderComponent implements OnInit {
  public userProfileIcon   = this.sanitizer.bypassSecurityTrustHtml(userProfileIcon);

  warnings : Array<SessionWarningModel>;

  constructor (
    public companyService               : CompanyService,
    private apiService                  : ApiService,
    private businessIntelligenceService : BusinessIntelligenceService,
    private confirmService              : ConfirmService,
    private messagesService             : MessagesService,
    private modalService                : NgbModal,
    private myobService                 : MyobService,
    public pluralizePipe                : PluralizePipe,
    private quickbooksService           : QuickbooksService,
    private router                      : UIRouter,
    private sanitizer                   : DomSanitizer,
    public sessionService               : SessionService,
    private sidebarService              : SidebarService,
    private translateService            : TranslateService,
    private xeroService                 : XeroService
  ) {}

  ngOnInit () : void {
    this.warnings = this.sessionService.getWarnings();
  }

  create ( state = 'app' ) : void {
    this.router.stateService.go(state, undefined, {
      inherit: false
    });

    this.resetSidebar();
  }

  dismissWarning ( warning = { id: '*' }) : void {
    this.apiService.get('/company/dismiss_notification', warning.id)
    .then(( warnings : Array<SessionWarningModel> ) => {
      this.warnings = warnings;
    });
  }

  hasRights ( module : string ) : boolean {
    return this.sessionService.hasRights(module);
  }

  logout () : void {
    this.apiService.delete('/sessions')
    .finally(() => {
      this.businessIntelligenceService.logout();

      this.sessionService.clear();

      window.location.assign('login.html');
    });
  }

  resetSidebar () : void {
    this.sidebarService.reset();
  }

  syncMyob () {
    if (this.companyService.isLinkedToBranch() && this.companyService.isSharedAccounting() && !this.companyService.company.last_myob_sync_down) {
      this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.LINK_TOOL'), undefined, {
        cancelText : 'GENERAL_SPACE.BUTTON.CONTACTS_NO',
        confirmText: 'GENERAL_SPACE.BUTTON.CONTACTS_YES',
        size       : 'lg'
      }).then(() => {
        this.router.stateService.go('app.branches.contacts');
      })
      .catch(() => {
        this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.CONFIRM_SYNC'))
        .then(() => this.myobService.sync())
        .then(() => {
          this.messagesService.show(this.translateService.instant('JS_SPACE.MYOB.SYNC_CONFIRM'), 'success');
        })
        .catch(() => {
          this.apiService.get('myob/ignore_sync_before')
          .then(() => this.myobService.sync())
          .then(() => {
              this.messagesService.show(this.translateService.instant('JS_SPACE.MYOB.SYNC_CONFIRM'), 'success');
          });
        });
      });
    }
    else {
      return this.myobService.sync()
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.MYOB.SYNC_CONFIRM'), 'success');
      });
    }
  }

  syncQuickbooks () {
    if (this.companyService.isLinkedToBranch() && this.companyService.isSharedAccounting() && !this.companyService.company.last_qbo_sync_down) {
      this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.LINK_TOOL'), undefined, {
        cancelText : 'GENERAL_SPACE.BUTTON.CONTACTS_NO',
        confirmText: 'GENERAL_SPACE.BUTTON.CONTACTS_YES',
        size       : 'lg'
      })
      .then(() => {
        this.router.stateService.go('app.branches.contacts');
      })
      .catch(() => {
        this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.CONFIRM_SYNC'))
        .then(() => this.quickbooksService.sync())
        .then(() => {
          this.messagesService.show(this.translateService.instant('JS_SPACE.QUICKBOOKS.SYNC_CONFIRM'), 'success');
        })
        .catch(() => {
          this.apiService.get('qbo/ignore_sync_before')
          .then(() => this.quickbooksService.sync())
          .then(() => {
            this.messagesService.show(this.translateService.instant('JS_SPACE.QUICKBOOKS.SYNC_CONFIRM'), 'success');
          });
        });
      });
    }
    else {
      this.quickbooksService.sync()
      .then(() => {
        this.messagesService.show(this.translateService.instant('JS_SPACE.QUICKBOOKS.SYNC_CONFIRM'), 'success');
      });
    }
  };

  syncXero () : void {
    if (this.companyService.isLinkedToBranch() && this.companyService.isSharedAccounting() && !this.companyService.company.last_xero_sync_down) {
      this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.LINK_TOOL'), undefined, {
        cancelText : 'GENERAL_SPACE.BUTTON.CONTACTS_NO',
        confirmText: 'GENERAL_SPACE.BUTTON.CONTACTS_YES',
        size       : 'lg'
      })
      .then(() => this.router.stateService.go('app.branches.contacts'))
      .catch(() => {
        this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.CONFIRM_SYNC'))
        .then(() => {
          this.xeroService.sync()
          .then(( conflicts : number ) => {
            if (conflicts) {
              this.router.stateService.go('app.xero-contacts');
            }
            else {
              this.messagesService.show(this.translateService.instant('JS_SPACE.XERO.SYNC_CONFIRM'), 'success');
            }
          });
        })
        .catch(() => {
          this.apiService.get('xero/ignore_sync_before')
          .then(() => {
            return this.xeroService.sync()
            .then(( conflicts : number ) => {
              if (conflicts) {
                this.router.stateService.go('app.xero-contacts');
              }
              else {
                this.messagesService.show(this.translateService.instant('JS_SPACE.XERO.SYNC_CONFIRM'), 'success');
              }
            });
          });
        });
      });
    }
    else {
      this.xeroService.sync()
      .then(( conflicts : number ) => {
        if (conflicts) {
          this.router.stateService.go('app.xero-contacts');
        }
        else {
          this.messagesService.show(this.translateService.instant('JS_SPACE.XERO.SYNC_CONFIRM'), 'success');
        }
      });
    }
  }
}
